import moment from 'moment';

import { NgbModal }         from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { UIRouter }         from '@uirouter/core';

import {
  Component,
  ElementRef,
  Input,
  NgZone,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';

import { BookingDiaryAppointmentModalComponent } from './booking-diary.appointment-modal.component';
import { BookingDiaryBookingModalComponent }     from './booking-diary.booking-modal.component';
import { BookingDiaryBookingDraftModel }         from './typings/booking-diary.draft';
import { BookingDiaryService }                   from './booking-diary.service';
import { BookingDiaryStartJobsModalComponent }   from './booking-diary.start-jobs-modal.component';
import { BookingDiaryTimeGridComponent }         from './booking-diary.time-grid.component';
import { BookingDiaryUnassignedRailComponent }   from './booking-diary.unassigned-rail.component';
import { CompanyService }                        from '../companies/company.service';
import { ConfirmService }                        from '../messages/confirm/confirm.service';
import { DropdownOptionModel }                   from '../shared/components/dropdown/typings/dropdown-option';
import { JobsService }                           from '../transaction-center/jobs/transaction-center.jobs.service';
import { MessagesService }                       from '../messages/messages.service';
import { MessengerService }                      from '../messages/messenger/messenger.service';
import { SessionService }                        from '../sessions/session.service';
import { TabModel }                              from '../shared/components/tab-bar/typings/tab';

import { BookingDiaryEventModel }      from './typings/booking-diary.event';
import { BookingDiaryListActionModel } from './typings/booking-diary.list';

import {
  BookingDiaryAllBookingModel,
  BookingDiaryStatsModel,
  BookingDiaryUnassignedJobModel
} from './typings/booking-diary.backlog';

import {
  BookingDiaryDragMoveModel,
  BookingDiaryEventDropModel,
  BookingDiaryJobDropModel,
  BookingDiaryMoveModel,
  BookingDiaryResizeModel,
  BookingDiarySlotModel
} from './typings/booking-diary.drag';

import {
  BookingDiaryGroupModel,
  BookingDiaryMechanicModel
} from './typings/booking-diary.mechanic';

import {
  BookingDiaryMonthDayModel,
  BookingDiaryMonthMechanicClickModel,
  BookingDiaryMonthTotalsModel
} from './typings/booking-diary.month';

import {
  BookingDiaryColumnModel,
  BookingDiaryFoldedDaysModel,
  BookingDiaryListMode,
  BookingDiaryViewType
} from './typings/booking-diary.view';

import {
  readFoldedDays,
  writeFoldedDay
} from './booking-diary.weekend';

import {
  clampMinutes,
  DATE_FORMAT,
  minutesFromTime,
  monthRangeWeeksFor,
  SLOT_MINUTES,
  weekDatesFor
} from './booking-diary.layout';

import { byMechanicName } from './booking-diary.options';

const ALL_LIST_KEY          : string = 'bookingDiaryAllList';
const ALL_LIST_PAGE_SIZE    : number = 50;
const ALL_MECHANICS         : string = '';
const CONTINUOUS_VIEW_KEY   : string = 'bookingDiaryContinuousView';
const DEFAULT_MAX_MECHANICS : number = 5;
const DEFAULT_SHOP_CLOSES   : string = '18:00:00';
const DEFAULT_SHOP_OPENS    : string = '08:00:00';
const LIST_VIEW_KEY         : string = 'bookingDiaryListView';
const MAX_EXTENDED_MONTHS   : number = 12;
// split parts a booking may have waiting in Unassigned before it can be split again
const MAX_WAITING_SPLITS    : number = 2;
const MECHANICS_VIEW_KEY    : string = 'bookingDiaryMechanicsView';
// unassigned cards the rail shows before it points to the list
const RAIL_LIMIT            : number = 10;
const SPLIT_HOURS           : number = 1;
// the backlog endpoint allows 500 rows and one more is asked for, so the whole backlog is one under
const UNASSIGNED_ALL_SIZE   : number = 499;
const UNASSIGNED_LIST_KEY   : string = 'bookingDiaryUnassignedList';
const WEEK_LANE_WINDOW      : number = 8;

const VIEWS : Array<BookingDiaryViewType> = [ 'day', 'week', 'month' ];

// oldest date first and undated last; on a day a booking's parts stay together, in split order
function sortByAnchor ( a : BookingDiaryUnassignedJobModel, b : BookingDiaryUnassignedJobModel ) : number {
  const aDate = String(a.event_date || '').slice(0, 10);
  const bDate = String(b.event_date || '').slice(0, 10);

  if ( aDate !== bDate ) {
    return aDate && bDate
      ? aDate.localeCompare(bDate)
      : bDate.localeCompare(aDate);
  }

  return String(a.display_name || '').localeCompare(String(b.display_name || ''))
    || String(a.event_id || '').localeCompare(String(b.event_id || ''))
    || ( a.ordering || 0 ) - ( b.ordering || 0 );
}

const EMPTY_GROUP : BookingDiaryGroupModel = {
  mechanics: [],
  name     : ''
};

// The diary page: header, tools and the day or week grid; page styles live under .booking-diary
@Component({
  encapsulation: ViewEncapsulation.None,
  selector     : 'wor-booking-diary',
  styles: [ require('./booking-diary.component.scss') ],
  template: require('./booking-diary.component.html')
})
export class BookingDiaryComponent implements OnInit {
  @Input() initialDate  : string;
  @Input() initialView  : string;

  @ViewChild(BookingDiaryUnassignedRailComponent, { read: ElementRef }) unassignedRail : ElementRef<HTMLElement>;

  @ViewChild(BookingDiaryTimeGridComponent)                             timeGrid       : BookingDiaryTimeGridComponent;

  allBookingsPage     : number                                   = 1;
  allBookingsPageSize : number                                   = ALL_LIST_PAGE_SIZE;
  allBookingsTotal    : number                                   = 0;
  bookingRequestCount : number                                   = 0;
  columnKey           : 'mechanicId' | 'date'                    = 'mechanicId';
  columns             : Array<BookingDiaryColumnModel>           = [];
  date                : string;

  // held, not derived: a drag runs change detection per move and a getter would re-render the rail
  dayJobs             : Array<BookingDiaryUnassignedJobModel>    = [];
  dragGhost           : BookingDiaryDragMoveModel                = null;
  events              : Array<BookingDiaryEventModel>            = [];
  foldedDays          : BookingDiaryFoldedDaysModel              = {};
  groups              : Array<BookingDiaryGroupModel>            = [];
  hasError            : boolean                                  = false;
  hasMoreJobs         : boolean                                  = false;
  isAllList           : boolean                                  = false;
  isContinuousView    : boolean                                  = false;
  isGoldOrHigher      : boolean                                  = false;
  isListView          : boolean                                  = false;
  isLoading           : boolean                                  = true;
  isLoadingAll        : boolean                                  = false;
  isLoadingMonths     : boolean                                  = false;
  isMechanicsView     : boolean                                  = true;
  isRailDropActive    : boolean                                  = false;
  isRefreshing        : boolean                                  = false;
  columnScroll        : { canNext : boolean; canPrev : boolean } = null;
  isUnassignedList    : boolean                                  = false;
  jobs                : Array<BookingDiaryUnassignedJobModel>    = [];
  laneOrder           : Array<string>                            = [];
  laneScroll          : { canNext : boolean; canPrev : boolean } = null;

  // held rather than derived, so the list is not handed a fresh array on every pass
  listBookings        : Array<BookingDiaryAllBookingModel>       = [];
  listJobs            : Array<BookingDiaryUnassignedJobModel>    = [];
  mechanicOptions     : Array<DropdownOptionModel>               = [];
  monthDays           : Array<BookingDiaryMonthDayModel>         = [];
  monthInView         : string                                   = '';
  monthWeeks          : Array<Array<BookingDiaryMonthDayModel>>  = [];
  railColors          : { [jobId : string] : string }            = {};
  railJobs            : Array<BookingDiaryUnassignedJobModel>    = [];
  requestsMessage     : string                                   = '';
  selectedMechanicId  : string                                   = ALL_MECHANICS;
  shopClosesMinutes   : number                                   = minutesFromTime(DEFAULT_SHOP_CLOSES);
  shopOpensMinutes    : number                                   = minutesFromTime(DEFAULT_SHOP_OPENS);
  stats               : BookingDiaryStatsModel                   = {};
  view                : BookingDiaryViewType                     = 'day';
  viewTabs            : Array<TabModel>                          = [];
  weekdayLabels       : Array<string>                            = [];

  private dayColumnCapacity : number                                             = 0;
  private dayColumnOffset   : number                                             = 0;
  private earlierMonths     : number                                             = 0;
  private extendedMonths    : number                                             = 1;
  private listEventsSorted  : Array<BookingDiaryEventModel>                      = [];
  private listEventsSource  : Array<BookingDiaryEventModel>                      = null;
  private loadGeneration    : number                                             = 0;
  private monthRequests     : { [date : string] : number }                       = {};
  private monthTotals       : { [date : string] : BookingDiaryMonthTotalsModel } = {};
  private railGeneration    : number                                             = 0;
  private weekAllLanes      : Array<string>                                      = [];
  private weekLaneOffset    : number                                             = 0;

  constructor (
    private bookingDiaryService : BookingDiaryService,
    private companyService      : CompanyService,
    private confirmService      : ConfirmService,
    private jobsService         : JobsService,
    private messagesService     : MessagesService,
    private messengerService    : MessengerService,
    private modalService        : NgbModal,
    private ngZone              : NgZone,
    private router              : UIRouter,
    private sessionService      : SessionService,
    private translateService    : TranslateService
  ) {}

  ngOnInit () : void {
    this.foldedDays       = readFoldedDays();
    this.isAllList        = localStorage.getItem(ALL_LIST_KEY) === 'true';
    this.isContinuousView = localStorage.getItem(CONTINUOUS_VIEW_KEY) === 'true';
    this.isListView       = localStorage.getItem(LIST_VIEW_KEY) === 'true';
    this.isMechanicsView  = localStorage.getItem(MECHANICS_VIEW_KEY) !== 'false';
    this.isUnassignedList = localStorage.getItem(UNASSIGNED_LIST_KEY) === 'true';

    this.date = this.initialDate && moment(this.initialDate, DATE_FORMAT, true).isValid()
      ? this.initialDate
      : moment().format(DATE_FORMAT);

    this.view = VIEWS.indexOf(this.initialView as BookingDiaryViewType) !== -1
      ? this.initialView as BookingDiaryViewType
      : 'month';

    this.isGoldOrHigher = !!this.sessionService.base().gold_or_higher;
    this.weekdayLabels  = weekDatesFor(this.date).map(date => moment(date, DATE_FORMAT).format('ddd'));
    this.viewTabs       = VIEWS.map(view => ( {
      label: this.translateService.instant(`JS_SPACE.TABS.${ view.toUpperCase() }`),
      value: view
    } ));

    this.init();
  }

  get activeMechanics () : Array<BookingDiaryMechanicModel> {
    return this.selectedMechanicId
      ? this.group.mechanics.filter(mechanic => mechanic.id === this.selectedMechanicId)
      : this.group.mechanics;
  }

  get canReachEarlierMonths () : boolean {
    return this.isContinuousView && !this.isLoadingMonths && this.earlierMonths < MAX_EXTENDED_MONTHS;
  }

  clearFilters () : void {
    this.onMechanicSelected(ALL_MECHANICS);
  }

  // a booking from a month day starts unassigned on that day at the first time still ahead; one
  // from the header has no day yet, so the modal asks for it
  createBooking ( date : string = null ) : void {
    if ( !date ) {
      this.openBookingModal(null, null, this.shopOpensMinutes);

      return;
    }

    const today = moment().format(DATE_FORMAT);
    const day   = date < today
      ? today
      : date;

    this.openBookingModal(day, null, this.firstStartOn(day));
  }

  get dateLabel () : string {
    if ( this.isDateless ) {
      return this.translateService.instant(this.isUnassignedList
        ? 'BOOKING_SPACE.DIARY.ALL_UNASSIGNED_BOOKINGS'
        : 'BOOKING_SPACE.DIARY.ALL_BOOKINGS');
    }

    if ( this.view === 'week' ) {
      const dates = weekDatesFor(this.date);

      return `${ moment(dates[0], DATE_FORMAT).format('D MMM') } – ${ moment(dates[6], DATE_FORMAT).format('D MMM YYYY') }`;
    }

    if ( this.view === 'month' ) {
      return moment(this.isContinuousView && this.monthInView || this.date, DATE_FORMAT).format('MMMM YYYY');
    }

    return moment(this.date, DATE_FORMAT).format('dddd, D MMMM YYYY');
  }

  // Today opens today's day from any view; in the month it would otherwise change nothing
  goToToday () : void {
    this.view = 'day';

    this.resetBoard();
    this.changeDate(moment().format(DATE_FORMAT));
  }

  // the diary shows every mechanic at once, so there is only ever the one group
  get group () : BookingDiaryGroupModel {
    return this.groups[0] || EMPTY_GROUP;
  }

  init () : void {
    this.hasError  = false;
    this.isLoading = true;

    Promise.all([
      this.bookingDiaryService.getCompany(),
      this.bookingDiaryService.getGroups(this.groupsDate(), this.maximumMechanics()),
      this.bookingDiaryService.getBookingRequestCount()
    ])
      .then(([ company, groups, bookingRequestCount ]) => {
        this.shopOpensMinutes  = minutesFromTime(company.shop_opens || DEFAULT_SHOP_OPENS);
        this.shopClosesMinutes = minutesFromTime(company.shop_closes || DEFAULT_SHOP_CLOSES);

        if ( this.shopClosesMinutes <= this.shopOpensMinutes ) {
          this.shopClosesMinutes = 24 * 60 - 1;
        }

        this.bookingRequestCount = bookingRequestCount || 0;
        this.groups              = this.mergeGroups(groups);
        this.requestsMessage     = this.translateService.instant('BOOKING_SPACE.DIARY.REQUESTS_PENDING', {
          count: this.bookingRequestCount
        });

        this.buildMechanicOptions();

        return this.load();
      })
      .catch(() => {
        this.hasError  = true;
        this.isLoading = false;
      });
  }

  // the all-bookings list is not about a date, so the date controls rest while it shows
  get isDateless () : boolean {
    return this.isListView && this.isAllList;
  }

  // unassigned bookings have no mechanic yet, so the mechanic filter rests while they show
  get isMechanicless () : boolean {
    return this.showsUnassignedList;
  }

  // the continuous month scrolls both ways rather than paging, so the arrows have nothing to do
  get isMonthScrolling () : boolean {
    return this.view === 'month' && this.isContinuousView;
  }

  get isToday () : boolean {
    return this.date === moment().format(DATE_FORMAT);
  }

  get listEmptyMessage () : string {
    switch ( this.listMode ) {
      case 'all':
        return this.translateService.instant('BOOKING_SPACE.DIARY.NO_BOOKINGS_YET');
      case 'unassigned':
        return this.translateService.instant(this.isAllList
          ? 'BOOKING_SPACE.DIARY.NO_UNASSIGNED'
          : 'BOOKING_SPACE.DIARY.NO_UNASSIGNED_IN_PERIOD');
      default:
        return this.translateService.instant('BOOKING_SPACE.DIARY.NO_BOOKINGS_IN_PERIOD');
    }
  }

  // sorted once per events array, since the list asks on every pass
  get listEvents () : Array<BookingDiaryEventModel> {
    if ( this.listEventsSource !== this.events ) {
      this.listEventsSource = this.events;
      this.listEventsSorted = this.events
        .filter(event => event.kind === 'booking')
        .sort(( a, b ) => a.date === b.date
          ? a.startMinutes - b.startMinutes
          : a.date.localeCompare(b.date));
    }

    return this.listEventsSorted;
  }

  get listMode () : BookingDiaryListMode {
    if ( this.showsUnassignedList ) {
      return 'unassigned';
    }

    if ( this.showsAllList ) {
      return 'all';
    }

    return this.view === 'month'
      ? 'days'
      : 'events';
  }

  nextPeriod () : void {
    this.changeDate(this.stepDate(1));
  }

  onAllListChange () : void {
    localStorage.setItem(ALL_LIST_KEY, String(this.isAllList));

    this.allBookingsPage = 1;

    this.resetBoard();
    this.load();
  }

  // the grid reports how many mechanics fit at their narrowest; the day shows that many at once
  onColumnCapacityChanged ( capacity : number ) : void {
    this.dayColumnCapacity = capacity;

    if ( this.view === 'day' ) {
      this.buildColumns();
    }
  }

  // the day's arrows turn a whole page of mechanics at a time
  onColumnScrolled ( step : number ) : void {
    this.dayColumnOffset += step * this.dayColumnWindow();

    this.buildColumns();
  }

  // a week's heading opens that day; a day's heading shows that mechanic alone, or everyone again
  onColumnSelected ( columnId : string ) : void {
    if ( this.view === 'week' ) {
      this.onDayClick(columnId);

      return;
    }

    this.onMechanicSelected(this.selectedMechanicId === columnId
      ? ALL_MECHANICS
      : columnId);
  }

  onContinuousViewChange () : void {
    localStorage.setItem(CONTINUOUS_VIEW_KEY, String(this.isContinuousView));

    this.load();
  }

  onDayClick ( date : string ) : void {
    this.view = 'day';

    this.resetBoard();
    this.changeDate(date);
  }

  // a fresh object so the grids see the change; the now line is measured again from it
  onDayFoldToggled ( index : number ) : void {
    this.foldedDays = { ...this.foldedDays, [index]: !this.foldedDays[index] };

    writeFoldedDay(index, this.foldedDays[index]);
  }

  onEventCancelled ( event : BookingDiaryEventModel ) : void {
    this.cancelAppointment(event.id);
  }

  // the zone is re-entered only when something the template shows changed, or every move repaints
  onEventDragMoved ( move : BookingDiaryDragMoveModel ) : void {
    const ghost = move && move.isOutsideGrid && move.event.kind === 'booking'
      ? move
      : null;

    const isOverRail = !!ghost
      && this.view === 'day'
      && this.isPointOverRail(move.x, move.y);

    if ( !ghost && !this.dragGhost && isOverRail === this.isRailDropActive ) {
      return;
    }

    this.ngZone.run(() => {
      this.dragGhost        = ghost;
      this.isRailDropActive = isOverRail;
    });
  }

  // a booking dropped on the rail leaves the diary; dropped elsewhere off the grid, nothing changes
  onEventDroppedOutside ( drop : BookingDiaryEventDropModel ) : void {
    const pointer = drop.pointer || {
      x: drop.rect.left + drop.rect.width / 2,
      y: drop.rect.top + drop.rect.height / 2
    };

    this.isRailDropActive = false;

    if ( this.view !== 'day' || drop.event.kind !== 'booking' || !this.isPointOverRail(pointer.x, pointer.y) ) {
      return;
    }

    this.runAndReload(this.bookingDiaryService.unassignBooking(drop.event, this.date), 'BOOKING_SPACE.DIARY.MOVED_TO_UNASSIGNED');
  }

  // the board takes the new slot at once and only springs back if the save is refused
  onEventMoved ( move : BookingDiaryMoveModel ) : void {
    const targetDate = this.view === 'week'
      ? move.columnId
      : this.date;

    const targetMechanicId = this.view === 'week'
      ? ( move.laneId || move.event.mechanicId )
      : move.columnId;

    if ( this.hasPassed(targetDate, move.startMinutes) ) {
      this.onSlotPassed();

      return;
    }

    const from = { ...move.event };

    this.placeEvent(move.event, targetDate, targetMechanicId, move.startMinutes);

    this.runAndReload(
      this.bookingDiaryService.rescheduleEvent(from, targetDate, move.startMinutes, targetMechanicId),
      'JS_SPACE.MESSAGES.SAVED.EVENT_TIME',
      () => this.placeEvent(move.event, from.date, from.mechanicId, from.startMinutes)
    );
  }

  onEventResized ( resize : BookingDiaryResizeModel ) : void {
    const from    = { ...resize.event };
    // event.date, not this.date: in the week view the appointment belongs to the booking's own day
    const date    = resize.event.date || this.date;
    const resized = { ...from, startMinutes: resize.startMinutes };

    this.stretchEvent(resize.event, resize.startMinutes, resize.endMinutes);

    this.runAndReload(
      this.bookingDiaryService.resizeBooking(resized, date, resize.endMinutes),
      'JS_SPACE.MESSAGES.SAVED.EVENT_TIME',
      () => this.stretchEvent(resize.event, from.startMinutes, from.endMinutes)
    );
  }

  // event.date, not this.date: in the week view the appointment belongs to the booking's own day
  onEventSplit ( event : BookingDiaryEventModel ) : void {
    this.splitAppointment(event.eventId, event.eventItemId, event.date);
  }

  onJobCancelled ( job : BookingDiaryUnassignedJobModel ) : void {
    this.cancelAppointment(job.id);
  }

  // the job lands in the slot at once and returns to the rail only if the save is refused
  onJobDropped ( drop : BookingDiaryJobDropModel ) : void {
    const target = this.timeGrid
      ? this.timeGrid.locateFromRect({
        left : drop.point.x,
        top  : drop.point.y,
        width: 0
      } as ClientRect)
      : null;

    if ( !target ) {
      return;
    }

    const duration     = Math.round(( parseFloat(String(drop.job.estimated_hours)) || 1 ) * 60);
    const startMinutes = clampMinutes(target.startMinutes, this.shopOpensMinutes, this.shopClosesMinutes - duration);

    if ( this.hasPassed(this.date, startMinutes) ) {
      this.onSlotPassed();

      return;
    }

    if ( this.timeGrid.isOccupied(null, target.columnId, target.laneId, startMinutes, startMinutes + duration) ) {
      this.onMoveBlocked();

      return;
    }

    const placed      = this.eventFromJob(drop.job, this.date, startMinutes, target.columnId);
    const heldJobs    = this.jobs;
    const heldDayJobs = this.dayJobs;

    this.events  = this.events.concat(placed);
    this.jobs    = this.jobs.filter(job => job !== drop.job);
    this.dayJobs = this.dayJobs.filter(job => job !== drop.job);

    this.setRail();

    this.runAndReload(
      this.bookingDiaryService.bookUnassignedJob(drop.job, this.date, startMinutes, target.columnId),
      'JS_SPACE.MESSAGES.SAVED.EVENT_TIME',
      () => {
        this.events  = this.events.filter(event => event !== placed);
        this.jobs    = heldJobs;
        this.dayJobs = heldDayJobs;

        this.setRail();
      }
    );
  }

  onLaneScrolled ( step : number ) : void {
    this.weekLaneOffset += step;

    this.buildColumns();
  }

  onListAction ( action : BookingDiaryListActionModel ) : void {
    switch ( action.type ) {
      case 'delete':
        this.removeRow(action);
        break;
      case 'edit':
        this.editRow(action);
        break;
      default:
        this.messageCustomer(action, action.type === 'sms');
        break;
    }
  }

  onListPageChange ( page : number ) : void {
    this.allBookingsPage = page;

    this.load();
  }

  // only the continuous month keeps loading as its list scrolls; the all list pages instead
  onListReachedEnd () : void {
    if ( this.listMode === 'days' ) {
      this.onMonthScrollEnd();
    }
  }

  onListViewChange () : void {
    localStorage.setItem(LIST_VIEW_KEY, String(this.isListView));

    this.resetBoard();
    this.load();
  }

  onMechanicSelected ( mechanicId : string | number ) : void {
    this.selectedMechanicId = String(mechanicId || ALL_MECHANICS);
    this.allBookingsPage    = 1;
    this.dayColumnOffset    = 0;
    this.weekLaneOffset     = 0;

    this.load();
  }

  onMechanicsViewChange () : void {
    localStorage.setItem(MECHANICS_VIEW_KEY, String(this.isMechanicsView));
  }

  // a ring names one mechanic's day, so it opens that day held to them rather than the whole shop's
  onMonthMechanicClick ( click : BookingDiaryMonthMechanicClickModel ) : void {
    this.view               = 'day';
    this.selectedMechanicId = click.mechanicId;

    this.resetBoard();
    this.changeDate(click.date);
  }

  // the continuous month keeps appending months as the user scrolls down, up to a year out
  onMonthScrollEnd () : void {
    if ( !this.isContinuousView || this.isLoadingMonths || this.extendedMonths - this.earlierMonths >= MAX_EXTENDED_MONTHS ) {
      return;
    }

    this.loadMoreMonths();
  }

  onMonthScrollStart () : void {
    if ( !this.canReachEarlierMonths ) {
      return;
    }

    this.loadEarlierMonth();
  }

  onMonthShown ( month : string ) : void {
    this.monthInView = month;
  }

  onMoveBlocked () : void {
    this.messagesService.error(this.translateService.instant('BOOKING_SPACE.DIARY.SLOT_TAKEN'));
  }

  onSlotPassed () : void {
    this.messagesService.error(this.translateService.instant('BOOKING_SPACE.DIARY.SLOT_PASSED'));
  }

  // a past or taken slot cannot be booked into and says why; the modal saves, the diary reloads
  onSlotPicked ( slot : BookingDiarySlotModel ) : void {
    const date = this.view === 'week'
      ? slot.columnId
      : this.date;

    const mechanicId = this.view === 'week'
      ? slot.laneId
      : slot.columnId;

    if ( this.hasPassed(date, slot.startMinutes) ) {
      this.onSlotPassed();

      return;
    }

    if ( this.timeGrid.isOccupied(null, slot.columnId, slot.laneId, slot.startMinutes, slot.startMinutes + SLOT_MINUTES) ) {
      this.onMoveBlocked();

      return;
    }

    this.openBookingModal(date, mechanicId, slot.startMinutes);
  }

  onUnassignedListChange () : void {
    localStorage.setItem(UNASSIGNED_LIST_KEY, String(this.isUnassignedList));

    this.resetBoard();
    this.load();
  }

  onViewSelected ( value : string ) : void {
    const view = value as BookingDiaryViewType;

    if ( this.view === view ) {
      return;
    }

    // the day's mechanics are only who works that day, so moving to or from Day asks for them again
    const isGroupChange = ( this.view === 'day' ) !== ( view === 'day' );

    this.view = view;

    this.rememberLocation();
    this.resetBoard();

    if ( isGroupChange ) {
      this.changeDate(this.date);

      return;
    }

    this.load();
  }

  // the card saves for real and closes; the diary reports and reloads
  openAppointment ( event : BookingDiaryEventModel, job : BookingDiaryUnassignedJobModel = null ) : void {
    if ( !event.eventId ) {
      return;
    }

    const modalRef = this.modalService.open(BookingDiaryAppointmentModalComponent, {
      windowClass: 'booking-diary-modal-window'
    });

    const modal : BookingDiaryAppointmentModalComponent = modalRef.componentInstance;

    // a scheduled booking's last appointment goes back to the backlog; a backlog job's is cancelled
    modal.deleteAppointment = job
      ? () => {
        modalRef.dismiss();

        this.onJobCancelled(job);
      }
      : () => {
        modalRef.dismiss();

        this.removeAppointment(event);
      };

    modal.event             = event;
    modal.mechanics         = this.group.mechanics;
    modal.openBooking       = () => {
      modalRef.dismiss();

      this.openEvent(event);
    };
    modal.shopClosesMinutes = this.shopClosesMinutes;
    modal.shopOpensMinutes  = this.shopOpensMinutes;
    modal.unassignedJob     = job;

    modalRef.result
      .then(() => {
        this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.EVENT'));

        this.refreshBookings();
      })
      .catch(() => null);
  }

  openBooking ( booking : BookingDiaryAllBookingModel ) : void {
    this.router.stateService.go('app.event', {
      customer_id: booking.customer_id,
      id         : booking.id,
      vehicle_id : booking.vehicle_id
    });
  }

  // the row names a booking, so its day is read for the appointment; without one, the booking page
  openBookingCard ( booking : BookingDiaryAllBookingModel ) : void {
    if ( !booking.event_date ) {
      this.openBooking(booking);

      return;
    }

    const date = String(booking.event_date).slice(0, 10);

    this.bookingDiaryService.getDayEvents(date, this.activeMechanics)
      .then(events => {
        const event = events.find(known => known.kind === 'booking' && String(known.eventId) === String(booking.id));

        if ( event ) {
          this.openAppointment(event);

          return;
        }

        // a booking with no appointment on its day may still be in the backlog, with its own card
        return this.bookingDiaryService.getUnassignedJobs(UNASSIGNED_ALL_SIZE)
          .then(backlog => {
            const job = backlog.jobs.find(known => String(known.event_id) === String(booking.id));

            if ( job ) {
              this.openJob(job);
            }
            else {
              this.openBooking(booking);
            }
          });
      })
      .catch(() => {
        this.openBooking(booking);
      });
  }

  // a booking with a job card on it opens the job card, since that is where the work now lives
  openEvent ( event : BookingDiaryEventModel ) : void {
    if ( !event.eventId ) {
      return;
    }

    const hasJobCard = !!event.invoiceId;

    this.router.stateService.go(hasJobCard
      ? 'app.invoice'
      : 'app.event', {
      customer_id: event.customerId,
      id         : hasJobCard
        ? event.invoiceId
        : event.eventId,
      vehicle_id : event.vehicleId
    });
  }

  // the job is not on the grid, so the card opens on the day in view; a mechanic books it in
  openJob ( job : BookingDiaryUnassignedJobModel ) : void {
    if ( !job.event_id ) {
      return;
    }

    // the backlog row carries no vehicle, so the booking is read for it before the card opens
    this.bookingDiaryService.getEvent(job.event_id)
      .then(booking => {
        const date = job.event_date
          ? String(job.event_date).slice(0, 10)
          : this.date;

        this.openAppointment({
          ...this.eventFromJob(job, date, this.shopOpensMinutes, null),
          vehicleId: ( booking && booking.vehicle_id ) || null
        }, job);
      })
      .catch(err => {
        this.messagesService.error(err);
      });
  }

  openSchedule () : void {
    this.router.stateService.go('app.schedule');
  }

  previousPeriod () : void {
    this.changeDate(this.stepDate(-1));
  }

  printJobsForDate () : void {
    this.jobsService.printForDate()
      .catch(() => null);
  }

  get showsAllList () : boolean {
    return this.isListView && this.isAllList && !this.isUnassignedList;
  }

  get showsUnassignedList () : boolean {
    return this.isListView && this.isUnassignedList;
  }

  // the rail's link opens the day's unassigned bookings as the list, the same cards it counted
  showUnassignedList () : void {
    this.isAllList        = false;
    this.isListView       = true;
    this.isUnassignedList = true;

    localStorage.setItem(ALL_LIST_KEY, 'false');
    localStorage.setItem(LIST_VIEW_KEY, 'true');
    localStorage.setItem(UNASSIGNED_LIST_KEY, 'true');

    this.resetBoard();
    this.load();
  }

  // the day on screen is offered first; the job cards made change the chips, so the diary reloads
  startJobsForDate () : void {
    const modalRef = this.modalService.open(BookingDiaryStartJobsModalComponent, {
      windowClass: 'booking-diary-modal-window'
    });

    const modal : BookingDiaryStartJobsModalComponent = modalRef.componentInstance;

    modal.date = this.date;

    modalRef.result
      .then(() => this.load())
      .catch(() => null);
  }

  get startJobsTitle () : string {
    return this.translateService.instant('BOOKING_SPACE.DIARY.START_JOBS_ON', {
      date: moment(this.date, DATE_FORMAT).format('ddd D MMM')
    });
  }

  viewBookingRequests () : void {
    this.router.stateService.go('app.bookings.requests');
  }

  private buildColumns () : void {
    if ( this.view === 'week' ) {
      const format = this.companyService.language('usa')
        ? 'ddd M/D'
        : 'ddd D/M';

      this.columnKey = 'date';
      this.columns   = weekDatesFor(this.date).map(date => ( {
        id   : date,
        label: moment(date, DATE_FORMAT).format(format)
      } ));

      // a fixed lane per mechanic with bookings this week; a window of lanes shows at once
      this.weekAllLanes = this.activeMechanics
        .filter(mechanic => this.events.some(event => event.kind === 'booking' && event.mechanicId === mechanic.id))
        .map(mechanic => mechanic.id);

      const maxOffset = Math.max(this.weekAllLanes.length - WEEK_LANE_WINDOW, 0);

      this.weekLaneOffset = Math.min(Math.max(this.weekLaneOffset, 0), maxOffset);
      this.laneOrder      = this.weekAllLanes.slice(this.weekLaneOffset, this.weekLaneOffset + WEEK_LANE_WINDOW);
      this.laneScroll     = maxOffset > 0
        ? {
          canNext: this.weekLaneOffset < maxOffset,
          canPrev: this.weekLaneOffset > 0
        }
        : null;
      this.columnScroll   = null;

      return;
    }

    // as many mechanics as fit at their narrowest; the end headings page through the rest
    const columnWindow = this.dayColumnWindow();
    const maxOffset    = Math.max(this.activeMechanics.length - columnWindow, 0);

    this.dayColumnOffset = Math.min(Math.max(this.dayColumnOffset, 0), maxOffset);
    this.columnKey       = 'mechanicId';
    this.columns         = this.activeMechanics
      .slice(this.dayColumnOffset, this.dayColumnOffset + columnWindow)
      .map(mechanic => ( {
        id   : mechanic.id,
        label: mechanic.name
      } ));
    this.columnScroll    = maxOffset > 0
      ? {
        canNext: this.dayColumnOffset < maxOffset,
        canPrev: this.dayColumnOffset > 0
      }
      : null;
    this.laneOrder       = [];
    this.laneScroll      = null;
  }

  private buildMechanicOptions () : void {
    this.mechanicOptions = [
      {
        label: this.translateService.instant('BOOKING_SPACE.DIARY.ALL_MECHANICS'),
        value: ALL_MECHANICS
      },
      ...byMechanicName(this.group.mechanics).map(mechanic => ( {
        label: mechanic.name,
        value: mechanic.id
      } ))
    ];
  }

  // held, not derived: a getter would hand the dropdown a new array on every change detection
  private buildMonthWeeks () : void {
    const rangeEnd   = moment(this.date, DATE_FORMAT).add(this.monthsInView() - 1, 'month').endOf('month').format(DATE_FORMAT);
    const rangeStart = moment(this.date, DATE_FORMAT).startOf('month').format(DATE_FORMAT);
    const today      = moment().format(DATE_FORMAT);

    this.monthWeeks = monthRangeWeeksFor(this.date, this.monthsInView()).map(week => week.map(date => {
      const dayTotals = this.monthTotals[date];

      return {
        date,
        availableHours     : dayTotals ? dayTotals.availableHours : 0,
        bookedHours        : dayTotals ? dayTotals.bookedHours : 0,
        dayOfMonth         : parseInt(date.slice(8, 10), 10),
        hasAvailableLoanCar: dayTotals ? dayTotals.hasAvailableLoanCar : undefined,
        isInMonth            : date >= rangeStart && date <= rangeEnd,
        isToday            : date === today,
        jobs               : dayTotals ? dayTotals.jobs : 0,
        mechanics          : ( dayTotals && dayTotals.mechanics ) || [],
        monthLabel         : this.isContinuousView && date.slice(8, 10) === '01'
          ? moment(date, DATE_FORMAT).format('MMMM')
          : undefined,
        requests           : this.monthRequests[date] || 0,
        unassigned         : dayTotals ? dayTotals.unassigned : 0
      };
    }));

    this.markMonthBoundaries();

    this.monthDays = this.monthWeeks
      .reduce(( all, week ) => all.concat(week), [])
      .filter(day => day.isInMonth);
  }

  private cancelAppointment ( id : string ) : void {
    this.confirmService.generic(this.translateService.instant('BOOKING_SPACE.DIARY.CANCEL_APPOINTMENT'))
      .then(() => this.runAndReload(this.bookingDiaryService.cancelAppointment(id), 'JS_SPACE.MESSAGES.DELETED.EVENT_TIME'))
      .catch(() => null);
  }

  private changeDate ( date : string ) : void {
    this.date         = date;
    this.isRefreshing = true;

    this.bookingDiaryService.getGroups(this.groupsDate(), this.maximumMechanics())
      .then(groups => {
        this.groups = this.mergeGroups(groups);

        if ( this.selectedMechanicId && !this.group.mechanics.some(mechanic => mechanic.id === this.selectedMechanicId) ) {
          this.selectedMechanicId = ALL_MECHANICS;
        }

        this.rememberLocation();
        this.buildMechanicOptions();

        return this.load();
      })
      .catch(err => {
        this.isRefreshing = false;

        this.messagesService.error(err);
      });
  }

  // until the grid has measured itself every mechanic shows
  private dayColumnWindow () : number {
    return this.dayColumnCapacity || this.activeMechanics.length;
  }

  // a backlog row names an appointment, so its booking is read for the vehicle the page wants
  private editRow ( row : BookingDiaryListActionModel ) : void {
    if ( row.booking ) {
      this.openBooking(row.booking);

      return;
    }

    if ( row.event ) {
      this.openEvent(row.event);

      return;
    }

    if ( !row.job || !row.job.event_id ) {
      return;
    }

    this.bookingDiaryService.getEvent(row.job.event_id)
      .then(event => {
        this.router.stateService.go('app.event', {
          customer_id: event.customer_id,
          id         : event.id,
          vehicle_id : event.vehicle_id
        });
      })
      .catch(err => {
        this.messagesService.error(err);
      });
  }

  // a backlog job as the grid and the card see it, placed on a day at a time for a mechanic
  private eventFromJob ( job : BookingDiaryUnassignedJobModel, date : string, startMinutes : number, mechanicId : string ) : BookingDiaryEventModel {
    const duration = Math.round(( parseFloat(String(job.estimated_hours)) || 1 ) * 60);
    const mechanic = this.group.mechanics.find(known => known.id === mechanicId);

    return {
      date,
      mechanicId,
      startMinutes,
      color              : ( this.events.find(event => event.mechanicId === mechanicId) || { color: '' } ).color,
      customerDisplayName: job.display_name,
      customerId         : job.customer_id,
      description        : [ job.event_description, job.event_item_description ].filter(part => !!part).join(' - '),
      displayName        : mechanic
        ? mechanic.name
        : '',
      endMinutes         : startMinutes + duration,
      estimatedHours     : job.estimated_hours,
      eventId            : job.event_id,
      eventItemId        : job.event_item_id,
      id                 : String(job.id),
      jobStatus          : job.job_status,
      kind               : 'booking',
      lane               : 0,
      lanes              : 1,
      offset             : moment().format('Z'),
      ordering           : job.ordering,
      totalSplits        : job.total_splits
    };
  }

  // the shop's opening on a later day; today, the next slot still ahead, held inside opening hours
  private firstStartOn ( date : string ) : number {
    if ( date !== moment().format(DATE_FORMAT) ) {
      return this.shopOpensMinutes;
    }

    const minutesNow = moment().diff(moment().startOf('day'), 'minutes');

    return clampMinutes(
      Math.ceil(minutesNow / SLOT_MINUTES) * SLOT_MINUTES,
      this.shopOpensMinutes,
      this.shopClosesMinutes - SLOT_MINUTES
    );
  }

  // a week or month spans days with different rosters, so it asks for every mechanic
  private groupsDate () : string {
    return this.view === 'day'
      ? this.date
      : null;
  }

  // a slot is gone once its first quarter hour is behind the clock, on today or any earlier day
  private hasPassed ( date : string, startMinutes : number ) : boolean {
    const today      = moment().format(DATE_FORMAT);
    const minutesNow = moment().diff(moment().startOf('day'), 'minutes');

    return date < today || ( date === today && startMinutes + SLOT_MINUTES <= minutesNow );
  }

  private isPointOverRail ( x : number, y : number ) : boolean {
    if ( !this.unassignedRail ) {
      return false;
    }

    const rail = this.unassignedRail.nativeElement.getBoundingClientRect();

    return x >= rail.left && x <= rail.right && y >= rail.top && y <= rail.bottom;
  }

  // the grid knows the bookings it shows, so it answers for those; any other day is checked on save
  private isSlotFree ( date : string, mechanicId : string, startMinutes : number, endMinutes : number ) : boolean {
    if ( !mechanicId || !this.timeGrid || ( this.view === 'day' && date !== this.date ) ) {
      return true;
    }

    const columnId = this.view === 'week'
      ? date
      : mechanicId;

    const laneId = this.view === 'week'
      ? mechanicId
      : null;

    return !this.timeGrid.isOccupied(null, columnId, laneId, startMinutes, endMinutes);
  }

  // the all list and the whole backlog replace the view's own load; anything else rides on it
  private listLoader () : Promise<void> {
    if ( this.showsAllList ) {
      return this.loadAllBookings();
    }

    if ( this.showsUnassignedList && this.isAllList ) {
      return this.loadUnassignedJobs();
    }

    return null;
  }

  // each load carries a generation, so a late response for a date already left is ignored
  private load () : Promise<void> {
    const generation = ++this.loadGeneration;

    this.isRefreshing = true;

    const loader = this.listLoader() || this.viewLoader()
      .then(() => this.showsUnassignedList && this.view !== 'day'
        ? this.loadUnassignedJobs()
        : undefined);

    return loader
      .catch(err => {
        if ( generation === this.loadGeneration ) {
          this.messagesService.error(err);
        }
      })
      .then(() => {
        if ( generation === this.loadGeneration ) {
          this.isLoading    = false;
          this.isRefreshing = false;
        }
      });
  }

  // one page of every booking, or of the mechanic picked, newest first, with the count for the pager
  private loadAllBookings () : Promise<void> {
    const generation = this.loadGeneration;
    const offset     = ( this.allBookingsPage - 1 ) * ALL_LIST_PAGE_SIZE;

    this.isLoadingAll = true;

    return Promise.all([
      this.bookingDiaryService.getAllBookings(offset, ALL_LIST_PAGE_SIZE, this.selectedMechanicId),
      this.bookingDiaryService.getAllBookingsCount(this.selectedMechanicId)
    ])
      .then(([ bookings, total ]) => {
        if ( generation !== this.loadGeneration ) {
          return;
        }

        this.allBookingsTotal = total;
        this.listBookings     = bookings;
      })
      .catch(err => {
        this.isLoadingAll = false;

        throw err;
      })
      .then(() => {
        if ( generation === this.loadGeneration ) {
          this.isLoadingAll = false;
        }
      });
  }

  private loadDay () : Promise<void> {
    const generation = this.loadGeneration;

    return Promise.all([
      this.bookingDiaryService.getDayEvents(this.date, this.activeMechanics),
      this.bookingDiaryService.getUnassignedJobs(),
      this.bookingDiaryService.getStats(this.date)
    ])
      .then(([ events, backlog, stats ]) => {
        if ( generation !== this.loadGeneration ) {
          return;
        }

        this.events = events;
        this.stats  = stats;

        this.setBacklog(backlog.jobs, backlog.hasMore);

        this.buildColumns();
      });
  }

  // the month before joins the front of the range, so the heading follows it back
  private loadEarlierMonth () : void {
    const generation = this.loadGeneration;
    const monthDate  = moment(this.date, DATE_FORMAT).subtract(1, 'month').format(DATE_FORMAT);

    this.isLoadingMonths = true;

    this.bookingDiaryService.getMonthTotals(monthDate, this.activeMechanics)
      .then(totals => {
        if ( generation !== this.loadGeneration ) {
          return;
        }

        this.date            = monthDate;
        this.earlierMonths  += 1;
        this.extendedMonths += 1;
        this.monthTotals     = { ...this.monthTotals, ...totals };

        this.buildMonthWeeks();
      })
      .catch(err => {
        this.messagesService.error(err);
      })
      .then(() => {
        this.isLoadingMonths = false;
      });
  }

  private loadMonth () : Promise<void> {
    const generation = this.loadGeneration;

    this.earlierMonths  = 0;
    this.extendedMonths = this.isContinuousView
      ? 2
      : 1;

    return Promise.all([
      Promise.all(this.monthStarts().map(monthDate => this.bookingDiaryService.getMonthTotals(monthDate, this.activeMechanics))),
      this.bookingDiaryService.getBookingRequestDates()
    ])
      .then(([ totalsByMonth, requests ]) => {
        if ( generation !== this.loadGeneration ) {
          return;
        }

        this.monthRequests = requests;
        this.monthTotals   = Object.assign({}, ...totalsByMonth);

        this.buildMonthWeeks();
      });
  }

  private loadMoreMonths () : void {
    const generation = this.loadGeneration;
    const monthDate  = moment(this.date, DATE_FORMAT).add(this.extendedMonths, 'month').format(DATE_FORMAT);

    this.isLoadingMonths = true;

    this.bookingDiaryService.getMonthTotals(monthDate, this.activeMechanics)
      .then(totals => {
        if ( generation !== this.loadGeneration ) {
          return;
        }

        this.extendedMonths += 1;
        this.monthTotals     = { ...this.monthTotals, ...totals };

        this.buildMonthWeeks();
      })
      .catch(err => {
        this.messagesService.error(err);
      })
      .then(() => {
        this.isLoadingMonths = false;
      });
  }

  // the whole backlog for the unassigned list; the day view's own fetch covers the rail
  private loadUnassignedJobs () : Promise<void> {
    const generation = this.loadGeneration;

    return this.bookingDiaryService.getUnassignedJobs(UNASSIGNED_ALL_SIZE)
      .then(backlog => {
        if ( generation !== this.loadGeneration ) {
          return;
        }

        this.setBacklog(backlog.jobs, backlog.hasMore);
      });
  }

  private loadWeek () : Promise<void> {
    const generation = this.loadGeneration;

    return this.bookingDiaryService.getWeekEvents(this.date, this.activeMechanics)
      .then(events => {
        if ( generation !== this.loadGeneration ) {
          return;
        }

        this.events = events;

        this.buildColumns();
      });
  }

  // in the continuous view a stepped line marks where one month becomes the next
  private markMonthBoundaries () : void {
    if ( !this.isContinuousView ) {
      return;
    }

    this.monthWeeks.forEach(( week, weekIndex ) => {
      const start = week.findIndex(day => day.date.slice(8, 10) === '01');

      if ( start === -1 || ( weekIndex === 0 && start === 0 ) ) {
        return;
      }

      week.forEach(( day, index ) => {
        if ( index >= start ) {
          day.isMonthBoundary = true;
        }
      });

      const next = this.monthWeeks[weekIndex + 1];

      if ( next ) {
        next.forEach(( day, index ) => {
          if ( index < start ) {
            day.isMonthBoundary = true;
          }
        });
      }
    });
  }

  private maximumMechanics () : number {
    const company = this.sessionService.company();

    return ( company && company.company_settings && company.company_settings.booking_calendar_maximum_mechanics )
      || DEFAULT_MAX_MECHANICS;
  }

  // the server pages mechanics by the company's maximum per page; the diary shows them all in one
  private mergeGroups ( groups : Array<BookingDiaryGroupModel> ) : Array<BookingDiaryGroupModel> {
    const mechanics = groups
      .reduce(( all, group ) => all.concat(group.mechanics), [] as Array<BookingDiaryMechanicModel>)
      .filter(( mechanic, index, all ) => all.findIndex(other => other.id === mechanic.id) === index);

    return [ { mechanics, name: '' } ];
  }

  // the customer's number and address are not on a row, so they are read when one is messaged
  private messageCustomer ( row : BookingDiaryListActionModel, isSms : boolean ) : void {
    const customerId = row.booking
      ? row.booking.customer_id
      : row.event
        ? row.event.customerId
        : row.job && row.job.customer_id;

    if ( !customerId ) {
      return;
    }

    this.bookingDiaryService.getCustomer(customerId)
      .then(customer => {
        const address = isSms
          ? customer.mobile
          : customer.email;

        if ( !address ) {
          return Promise.reject(this.translateService.instant(isSms
            ? 'BOOKING_SPACE.DIARY.NO_MOBILE_ON_FILE'
            : 'BOOKING_SPACE.DIARY.NO_EMAIL_ON_FILE'));
        }

        return customer;
      })
      .then(customer => isSms
        ? this.messengerService.sms({
          customer_id: customer.id,
          name       : customer.display_name,
          send_to    : customer.mobile
        })
        : this.messengerService.email({
          customer_id  : customer.id,
          email_address: customer.email,
          name         : customer.display_name
        }))
      .then(() => {
        this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT'));
      })
      .catch(err => {
        // the messenger rejects with a dismissal reason on close; a failed lookup carries a message
        if ( err && ( typeof err === 'string' || err.message ) ) {
          this.messagesService.error(err);
        }
      });
  }

  private monthsInView () : number {
    return this.isContinuousView
      ? this.extendedMonths
      : 1;
  }

  private monthStarts () : Array<string> {
    const starts : Array<string> = [];

    for ( let index = 0; index < this.monthsInView(); index++ ) {
      starts.push(moment(this.date, DATE_FORMAT).add(index, 'month').format(DATE_FORMAT));
    }

    return starts;
  }

  // the grid answers whether a time is free when it holds the day; otherwise the modal asks on save
  private openBookingModal ( date : string, mechanicId : string, startMinutes : number ) : void {
    const modalRef = this.modalService.open(BookingDiaryBookingModalComponent, {
      windowClass: 'booking-diary-modal-window'
    });

    const modal : BookingDiaryBookingModalComponent = modalRef.componentInstance;

    modal.date              = date;
    modal.isSlotFree        = ( id, start, end ) => this.isSlotFree(date, id, start, end);
    modal.mechanicId        = mechanicId;
    modal.mechanics         = this.group.mechanics;
    modal.shopClosesMinutes = this.shopClosesMinutes;
    modal.shopOpensMinutes  = this.shopOpensMinutes;
    modal.startMinutes      = startMinutes;

    modalRef.result
      .then(( draft : BookingDiaryBookingDraftModel ) => {
        this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.EVENT'));

        this.showDraft(draft);
        this.refreshBookings();
      })
      .catch(() => null);
  }

  // the dates the diary has on screen, which the unassigned list is trimmed to
  private periodDates () : Array<string> {
    if ( this.view === 'day' ) {
      return [ this.date ];
    }

    if ( this.view === 'week' ) {
      return weekDatesFor(this.date);
    }

    return this.monthDays.map(day => day.date);
  }

  // the grid rebuilds from the array it is handed, so a moved booking needs a fresh array
  private placeEvent (
    event        : BookingDiaryEventModel,
    date         : string,
    mechanicId   : string,
    startMinutes : number
  ) : void {
    const duration = event.endMinutes - event.startMinutes;

    event.date         = date;
    event.mechanicId   = mechanicId;
    event.startMinutes = startMinutes;
    event.endMinutes   = startMinutes + duration;

    this.events = this.events.slice();
  }

  // a saved or moved booking changes the bookings alone, so only they are read again and the
  // unrostered and away blocks stay; the month and the lists have totals to redo, so they reload
  private refreshBookings () : Promise<void> {
    if ( this.view === 'month' || this.isListView ) {
      return this.load();
    }

    const generation = ++this.loadGeneration;

    this.isRefreshing = true;

    const bookings = this.view === 'week'
      ? this.bookingDiaryService.getWeekBookings(this.date, this.activeMechanics)
      : this.bookingDiaryService.getDayBookings(this.date, this.activeMechanics);
    const dayExtras = this.view === 'day'
      ? Promise.all([
        this.bookingDiaryService.getUnassignedJobs(),
        this.bookingDiaryService.getStats(this.date)
      ])
      : Promise.resolve(null);

    return Promise.all([ bookings, dayExtras ])
      .then(([ events, extras ]) => {
        if ( generation !== this.loadGeneration ) {
          return;
        }

        this.events = this.events
          .filter(event => event.kind !== 'booking')
          .concat(events);

        if ( extras ) {
          const [ backlog, stats ] = extras;

          this.stats = stats;

          this.setBacklog(backlog.jobs, backlog.hasMore);
        }

        this.buildColumns();
      })
      .catch(err => {
        if ( generation === this.loadGeneration ) {
          this.messagesService.error(err);
        }
      })
      .then(() => {
        if ( generation === this.loadGeneration ) {
          this.isRefreshing = false;
        }
      });
  }

  // the date and view ride in the url as dynamic parameters, so a reload or link lands here
  private rememberLocation () : void {
    this.router.stateService.go('.', {
      date: this.date,
      view: this.view
    }, { location: 'replace' });
  }

  // one of several appointments is cancelled; the last one goes back to unassigned instead
  private removeAppointment ( event : BookingDiaryEventModel ) : void {
    if ( event.totalSplits > 1 ) {
      this.onEventCancelled(event);

      return;
    }

    this.confirmService.generic(this.translateService.instant('BOOKING_SPACE.DIARY.REMOVE_LAST_APPOINTMENT'))
      .then(() => this.runAndReload(this.bookingDiaryService.unassignBooking(event, event.date), 'BOOKING_SPACE.DIARY.MOVED_TO_UNASSIGNED'))
      .catch(() => null);
  }

  // an all-bookings row is the booking itself, so its bin deletes the booking as its page does
  private removeRow ( row : BookingDiaryListActionModel ) : void {
    if ( row.event ) {
      this.removeAppointment(row.event);

      return;
    }

    if ( row.job ) {
      this.onJobCancelled(row.job);

      return;
    }

    if ( !row.booking ) {
      return;
    }

    const booking = row.booking;

    this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_EVENT', { num: booking.event_number }))
      .then(() => this.runAndReload(this.bookingDiaryService.deleteBooking(booking.id), 'JS_SPACE.MESSAGES.DELETED.EVENT'))
      .catch(() => null);
  }

  // a view change renders with what the last view left behind, so the board is cleared first
  private resetBoard () : void {
    this.events = [];

    this.buildColumns();
  }

  // a save the diary only reports: a message either way, a rollback on refusal, then a reload
  private runAndReload ( request : Promise<any>, successKey : string, rollback : () => void = null ) : Promise<void> {
    this.isRefreshing = true;

    return request
      .then(() => {
        this.messagesService.show(this.translateService.instant(successKey));
      })
      .catch(err => {
        if ( rollback ) {
          rollback();
        }

        this.messagesService.error(err);
      })
      .then(() => this.refreshBookings());
  }

  // the rail is the day on screen plus undated bookings and split parts, oldest first
  private setBacklog ( jobs : Array<BookingDiaryUnassignedJobModel>, hasMore : boolean ) : void {
    const dates = this.isAllList
      ? null
      : this.periodDates();

    this.jobs         = jobs;
    this.hasMoreJobs = hasMore;
    this.dayJobs      = jobs
      .filter(job => !job.event_date
        || String(job.event_date).slice(0, 10) === this.date
        || job.total_splits > 1)
      .sort(sortByAnchor);
    this.listJobs     = this.view === 'day' && !this.isAllList
      ? this.dayJobs
      : jobs
        .filter(job => !dates || !job.event_date || dates.indexOf(String(job.event_date).slice(0, 10)) !== -1)
        .sort(sortByAnchor);

    this.setRail();
  }

  // the rail's first cards, split parts coloured by the labour line's latest placed part on any day
  private setRail () : void {
    const generation : number                                     = ++this.railGeneration;
    const pending    : { [eventItemId : string] : Array<string> } = {};
    const previous   : { [jobId : string] : string }              = this.railColors;

    this.railJobs   = this.dayJobs.slice(0, RAIL_LIMIT);
    this.railColors = {};

    this.railJobs.forEach(job => {
      const sibling = this.events
        .filter(event => event.kind === 'booking'
          && !!event.mechanicId
          && String(event.eventId) === String(job.event_id))
        .sort(( a, b ) => ( b.ordering || 0 ) - ( a.ordering || 0 ))[0];

      if ( sibling ) {
        this.railColors[job.id] = sibling.color;
      }
      else if ( job.total_splits > 1 && job.event_item_id ) {
        // the last colour stays on the card while the line's parts are read again
        if ( previous[job.id] ) {
          this.railColors[job.id] = previous[job.id];
        }

        pending[job.event_item_id] = ( pending[job.event_item_id] || [] ).concat(job.id);
      }
    });

    Object.keys(pending).forEach(eventItemId => {
      this.bookingDiaryService.getItemMechanicColor(eventItemId)
        .then(color => {
          if ( generation !== this.railGeneration ) {
            return;
          }

          const colors = { ...this.railColors };

          // a line with no placed part any more loses the colour it was keeping
          pending[eventItemId].forEach(jobId => {
            if ( color ) {
              colors[jobId] = color;
            }
            else {
              delete colors[jobId];
            }
          });

          this.railColors = colors;
        })
        .catch(() => null);
    });
  }

  // the new booking goes on the grid from what was typed, so it is seen before it is read back;
  // one with no mechanic belongs to the rail and one off the days on screen has no place yet
  private showDraft ( draft : BookingDiaryBookingDraftModel ) : void {
    const dates = this.view === 'week'
      ? weekDatesFor(this.date)
      : [ this.date ];

    if ( !draft || !draft.mechanicId || this.view === 'month' || this.isListView || dates.indexOf(draft.date) === -1 ) {
      return;
    }

    const mechanic = this.group.mechanics.find(entry => entry.id === draft.mechanicId);
    const name     = mechanic ? mechanic.name : '';
    const booking  = this.bookingDiaryService.bookingFromDraft(draft, name);

    this.events = this.events.concat([ booking ]);

    this.buildColumns();
  }

  // a one-hour second appointment, refused while the booking already has parts waiting in the backlog
  private splitAppointment ( eventId : string, eventItemId : string, date : string ) : void {
    if ( !eventId || !eventItemId ) {
      return;
    }

    this.isRefreshing = true;

    this.bookingDiaryService.getUnassignedJobs(UNASSIGNED_ALL_SIZE)
      .then(backlog => {
        const waiting = backlog.jobs.filter(job => String(job.event_id) === String(eventId)).length;

        if ( waiting >= MAX_WAITING_SPLITS ) {
          this.isRefreshing = false;

          this.messagesService.error(this.translateService.instant('BOOKING_SPACE.DIARY.SPLIT_LIMIT'));

          return;
        }

        return this.runAndReload(
          this.bookingDiaryService.addAppointment(eventId, eventItemId, SPLIT_HOURS, date),
          'BOOKING_SPACE.DIARY.SPLIT_SAVED'
        );
      })
      .catch(err => {
        this.isRefreshing = false;

        this.messagesService.error(err);
      });
  }

  private stepDate ( direction : number ) : string {
    const current = moment(this.date, DATE_FORMAT);

    if ( this.view === 'week' ) {
      return current.add(direction * 7, 'days').format(DATE_FORMAT);
    }

    if ( this.view === 'month' ) {
      return current.add(direction, 'months').startOf('month').format(DATE_FORMAT);
    }

    return current.add(direction, 'days').format(DATE_FORMAT);
  }

  private stretchEvent ( event : BookingDiaryEventModel, startMinutes : number, endMinutes : number ) : void {
    event.startMinutes = startMinutes;
    event.endMinutes   = endMinutes;

    this.events = this.events.slice();
  }

  private viewLoader () : Promise<void> {
    if ( this.view === 'month' ) {
      return this.loadMonth();
    }

    return this.view === 'week'
      ? this.loadWeek()
      : this.loadDay();
  }
}
